import React, { useCallback, useEffect, useState } from 'react';

import { ModalContext } from '@notino/react-styleguide-v20';

import { TrackingContextProvider, useTrackingContext } from '@context/TrackingContext';
import { useOrderLimitNotificationModal } from '@hooks';
import { EOrderAsLastTimeState } from '@model';
import {
  trackFreeDeliveryModalClose,
  trackPaymentDialogBackAction,
  trackServiceInfoModalClose,
  useTrackCheckoutCloseRepeatedPurchase,
} from '@services/ecommerceService';
import { GeneralModalBody, ETextAlignment } from '@sharedComponents/GeneralModalBody';
import { MODAL_SETTINGS_DEFAULT, MODAL_SETTINGS_LARGE } from '@utils/constants';

import { useOrderAsLastTimeProcess } from '../components';
import { useCartPageContext, ECartPageState } from '../context';
import { createEcommerceOaltCheckout } from '../ecommerce';
import { messages } from '../messages';

import { useFreeDeliveryProductsModal } from './useFreeDeliveryProductsModal';
import { useGiftPackagingModal } from './useGiftPackagingModal';
import { useLoyaltyFreeDeliveryModal } from './useLoyaltyFreeDeliveryModal';
import { useOrderAsLastTimeModal } from './useOrderAsLastTimeModal';
import { useServicesInfoModal } from './useServicesInfoModal';

export const useCartPageModals = () => {
  const {
    state: { cartPageState, lastOrder, pricesSummary, products, serviceItems },
    actions: { setCartPageState },
    settings: {
      adyen: { clientKey },
    },
  } = useCartPageContext();

  const { getTimeFromInit } = useTrackingContext();
  const lastPageViewMs = React.useRef(0);
  TrackingContextProvider.useSubscribeToInitTimeReset(() => {
    lastPageViewMs.current = 0;
  });

  const { toggleModal, hideModal } = ModalContext.useModalContext();
  const trackCloseOalt = useTrackCheckoutCloseRepeatedPurchase();

  const [isOaltProcessing, setIsOaltProcessing] = useState(false);

  const onModalClose = useCallback(() => {
    hideModal();
    setCartPageState(ECartPageState.DEFAULT);
  }, [hideModal, setCartPageState]);

  const {
    actionComponent,
    onContinueToCheckout,
    setState,
    pageState: oaltPageState,
  } = useOrderAsLastTimeProcess(lastOrder, pricesSummary, onModalClose, setIsOaltProcessing, clientKey);

  const loyaltyFreeDeliveryModal = useLoyaltyFreeDeliveryModal(onModalClose);
  const giftPackagingModal = useGiftPackagingModal(onModalClose);
  const orderAsLastTimeModal = useOrderAsLastTimeModal(
    onModalClose,
    actionComponent,
    oaltPageState,
    onContinueToCheckout,
    isOaltProcessing,
  );
  const notificationModal = useOrderLimitNotificationModal(messages);
  const freeDeliveryProductsModal = useFreeDeliveryProductsModal(onModalClose);
  const servicesInfoModal = useServicesInfoModal(onModalClose);

  const onModalBackButton = useCallback(() => {
    trackPaymentDialogBackAction(getTimeFromInit() - lastPageViewMs.current);
    setState(EOrderAsLastTimeState.INFO_PAGE);
  }, [setState, getTimeFromInit]);

  const onOaltModalClose = useCallback(() => {
    if (lastOrder) {
      trackCloseOalt(createEcommerceOaltCheckout(lastOrder, products, pricesSummary, serviceItems), oaltPageState);
    }
    onModalClose();
    setState(EOrderAsLastTimeState.INFO_PAGE);
  }, [onModalClose, setState, trackCloseOalt, lastOrder, products, pricesSummary, serviceItems, oaltPageState]);

  const onFreeDeliveryModalClose = useCallback(() => {
    trackFreeDeliveryModalClose();
    hideModal();
    setCartPageState(ECartPageState.DEFAULT);
  }, [hideModal, setCartPageState]);

  const onServiceInfoClose = useCallback(() => {
    trackServiceInfoModalClose();
    hideModal();
    setCartPageState(ECartPageState.DEFAULT);
  }, [hideModal, setCartPageState]);

  useEffect(() => {
    switch (cartPageState) {
      case ECartPageState.GIFT_PACKAGING:
        toggleModal(giftPackagingModal, {
          ...MODAL_SETTINGS_LARGE,
          onClose: onModalClose,
        });
        break;
      case ECartPageState.ORDER_AS_LAST_TIME:
        toggleModal(orderAsLastTimeModal, {
          ...MODAL_SETTINGS_DEFAULT,
          onClose: onOaltModalClose,
          showBackButton: oaltPageState === EOrderAsLastTimeState.ACTION_PAGE,
          onBackButton: onModalBackButton,
          noBorders: true,
          disableOnBlurClose: true,
        });
        break;
      case ECartPageState.ORDER_LIMIT_NOTIFICATION:
        toggleModal(
          <GeneralModalBody
            headerText={notificationModal.header}
            messagesText={notificationModal.messages}
            textAlignment={ETextAlignment.START}
          />,
          {
            ...MODAL_SETTINGS_DEFAULT,
            onClose: onModalClose,
          },
        );
        break;
      case ECartPageState.BENEFIT_FREE_DELIVERY:
        toggleModal(loyaltyFreeDeliveryModal, {
          ...MODAL_SETTINGS_LARGE,
          onClose: onModalClose,
          noBorders: true,
        });
        break;
      case ECartPageState.FREE_DELIVERY_PRODUCTS:
        toggleModal(freeDeliveryProductsModal, {
          ...MODAL_SETTINGS_LARGE,
          onClose: onFreeDeliveryModalClose,
        });
        break;
      case ECartPageState.GIFT_PACKAGING_SERVICE_INFO_MODAL:
      case ECartPageState.INSURANCE_SERVICE_INFO_MODAL:
        toggleModal(servicesInfoModal, {
          ...MODAL_SETTINGS_LARGE,
          onClose: onServiceInfoClose,
          verticalOverflow: 'hidden',
        });
        break;
    }
  }, [
    loyaltyFreeDeliveryModal,
    giftPackagingModal,
    orderAsLastTimeModal,
    cartPageState,
    toggleModal,
    onModalClose,
    notificationModal,
    onModalBackButton,
    onOaltModalClose,
    oaltPageState,
    freeDeliveryProductsModal,
    onFreeDeliveryModalClose,
    servicesInfoModal,
    onServiceInfoClose,
  ]);
};
